'use client';

import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { documensoEmbedSrc, parseDocumensoSigningEmbed } from '@src/components/sco/Advisors/utils/documensoSigningEmbed';
import { useEffect, useMemo, useRef } from 'react';
import { LuExternalLink } from 'react-icons/lu';
const EMBED_HEIGHT_PX = 720;
type DocumensoSignEmbedProps = {
  signingUrl: string;
  /** Fill the parent instead of a fixed 720px iframe (used in the sign modal). */
  fill?: boolean;
  showFallbackLink?: boolean;
  onCompleted?: () => void;
  onRejected?: () => void;
  onError?: (message: string) => void;
};

/**
 * Cloud signing iframe for one recipient. Token comes from that party’s
 * `documensoSigningUrl` — never the counterpart’s URL.
 */
export const DocumensoSignEmbed = ({
  signingUrl,
  fill = false,
  showFallbackLink = true,
  onCompleted,
  onRejected,
  onError
}: DocumensoSignEmbedProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onCompletedRef = useRef(onCompleted);
  const onRejectedRef = useRef(onRejected);
  const onErrorRef = useRef(onError);
  onCompletedRef.current = onCompleted;
  onRejectedRef.current = onRejected;
  onErrorRef.current = onError;
  const parsed = useMemo(() => parseDocumensoSigningEmbed(signingUrl), [signingUrl]);
  const src = useMemo(() => parsed ? documensoEmbedSrc(parsed) : null, [parsed]);
  useEffect(() => {
    if (!parsed) {
      return;
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== parsed.host) {
        return;
      }
      if (iframeRef.current?.contentWindow !== event.source) {
        return;
      }
      const action = event.data?.action;
      if (action === 'document-completed') {
        onCompletedRef.current?.();
        return;
      }
      if (action === 'document-rejected') {
        onRejectedRef.current?.();
        return;
      }
      if (action === 'document-error') {
        const payload = event.data?.data;
        const message = typeof payload === 'string' ? payload : 'Signing failed in Documenso. Try Open in Documenso.';
        onErrorRef.current?.(message);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [parsed]);
  if (!parsed || !src) {
    return <Text color={'colorPalette.fg/60'} fontFamily={'longform'} fontSize={'sm'}>
        Signing link is missing. Use the Documenso email, or Check status after you sign.
      </Text>;
  }
  return <VStack align={'stretch'} flex={fill ? 1 : undefined} gap={fill ? 0 : 2} minHeight={0}>
      <Box bg={'bg.panel'} borderColor={'border.muted'} borderRadius={fill ? 'none' : 'md'} borderWidth={fill ? 0 : '1px'} flex={fill ? 1 : undefined} minHeight={fill ? 0 : undefined} overflow={'hidden'}>
        <iframe ref={iframeRef} allow={'clipboard-write'} height={fill ? '100%' : EMBED_HEIGHT_PX} src={src} style={{
        display: 'block',
        width: '100%',
        height: fill ? '100%' : EMBED_HEIGHT_PX,
        border: 0
      }} title={'Sign FAST agreement'} width={'100%'} />
      </Box>
      {showFallbackLink ? <HStack>
          <Button asChild size={'sm'} variant={'ghost'}>
            <a href={signingUrl} rel={'noreferrer'} target={'_blank'}>
              <LuExternalLink /> Open in Documenso
            </a>
          </Button>
        </HStack> : null}
    </VStack>;
};